import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChatOptions } from "../../ui/ChatOptions";
import type { SuggestedTopic } from "../../../domains/gpt/transcript";

/**
 * The confirmation card for the agent's declared-interest proposal: the topics
 * it detected, pre-selected, plus a confirm button. Nothing is saved until the
 * user taps — the agent may only propose.
 *
 * Rendered in the COMPOSER BAND, in place of the chat input (the same slot the
 * post-onboarding conversation swaps its widgets through) — so it reads as the
 * turn's pending question rather than as a card buried in the scrollback. Both
 * exits restore the input: closing dismisses, confirming makes the suggestion
 * filter itself out. It therefore carries no padding of its own — the band owns
 * the horizontal inset.
 *
 * Presentational on purpose: it owns the selection (pure UI machinery, like any
 * `ChatOptions` host) and knows nothing about the API. The caller supplies the
 * already-filtered topics and performs the write.
 */
export function TopicSuggestionCard({
  topics,
  onConfirm,
  onDismiss,
}: {
  topics: SuggestedTopic[];
  // The uids the user kept selected. Never called with an empty list (the
  // confirm button is disabled until at least one is selected).
  onConfirm: (uids: string[]) => void;
  onDismiss: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  // Pre-selected: the agent proposes what it detected, and unticking is the
  // correction — an empty card the user must fill in would be a worse trade.
  const [selectedIds, setSelectedIds] = useState(() =>
    topics.map((topic) => topic.uid)
  );

  return (
    <ChatOptions
      title={t("Add to your interests?")}
      select="multiple"
      options={topics.map((topic) => ({ id: topic.uid, label: topic.name }))}
      selectedIds={selectedIds}
      onSelect={(id) => {
        setSelectedIds((previous) =>
          previous.includes(id)
            ? previous.filter((uid) => uid !== id)
            : [...previous, id]
        );
      }}
      freeText={null}
      onContinue={() => {
        onConfirm(selectedIds);
      }}
      continueLabel={t("Add to my interests")}
      continueDisabled={selectedIds.length === 0}
      onClose={onDismiss}
      pager={null}
      placement="band"
    />
  );
}
